import type { FC } from 'react'

import { useState } from 'react'
import { Dice5, ShieldCheck, SlidersHorizontal } from 'lucide-react'
import { withAlpha } from '@/lib/color'
import CountUp from '@/components/landing/count-up'

export type Scheme = 'dark' | 'light'

export type DerivedTheme = {
    scheme: Scheme
    surface: string
    panel: string
    line: string
    text: string
    muted: string
    accent: string
    accentInk: string
    adjusted: boolean
    stageAccent: string
    ratios: {
        text: number
        muted: number
        ink: number
    }
}

interface ThemePreviewProps {
    theme: DerivedTheme
    radius: number
    roundness: number
    font: string
}

const STAGE = '#0B0B0D'
const WIN = '#4BE3A0'
const NUMERIC = 'var(--font-app-mono)'

const ThemePreview: FC<ThemePreviewProps> = ({ theme, radius, roundness, font }) => {
    const [roll, setRoll] = useState(73.42)
    const [rolls, setRolls] = useState(0)
    const [mode, setMode] = useState<'manual' | 'auto'>('manual')

    const control = Math.round(roundness * 20)
    const keyRadius = roundness >= 0.5 ? 999 : control
    const win = roll > 50

    const placeBet = () => {
        setRoll(Math.floor(Math.random() * 10001) / 100)
        setRolls(previous => previous + 1)
    }

    return (
        <div
            className='w-full overflow-hidden border shadow-[0_50px_120px_-50px_rgba(0,0,0,0.6)] transition-[border-radius,background-color,color] duration-300'
            style={{ background: theme.surface, color: theme.text, borderColor: theme.line, borderRadius: radius, fontFamily: font }}
        >
            <div className='flex h-12 items-center justify-between gap-3 border-b px-4' style={{ borderColor: theme.line }}>
                <div className='flex min-w-0 items-center gap-2.5'>
                    <span className='grid size-7 shrink-0 place-items-center' style={{ background: theme.panel, borderRadius: Math.min(control, 14) }}>
                        <Dice5 aria-hidden='true' className='size-4' />
                    </span>
                    <span className='text-sm font-medium'>Dice</span>
                    <span className='truncate text-[11px]' style={{ color: theme.muted, fontFamily: NUMERIC }}>Over 50.00</span>
                </div>

                <div className='flex items-center gap-2.5'>
                    <span className='hidden text-right leading-tight sm:block'>
                        <span className='block text-[10px]' style={{ color: theme.muted }}>Demo credits</span>
                        <span className='block text-[13px]' style={{ fontFamily: NUMERIC }}>1,000.00</span>
                    </span>
                    <span className='px-2 py-1 text-[10px]' style={{ background: theme.panel, borderRadius: Math.min(control, 12) }}>Demo</span>
                </div>
            </div>

            <div className='grid md:grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)]'>
                <div className='flex flex-col gap-4 p-4'>
                    <div className='flex gap-1 p-1' style={{ background: theme.panel, borderRadius: control + 4 }}>
                        {(['manual', 'auto'] as const).map(option => (
                            <button
                                key={option}
                                type='button'
                                onClick={() => setMode(option)}
                                aria-pressed={mode === option}
                                className='h-8 flex-1 text-xs capitalize transition-colors'
                                style={{
                                    background: mode === option ? theme.surface : 'transparent',
                                    color: mode === option ? theme.text : theme.muted,
                                    borderRadius: control
                                }}
                            >
                                {option}
                            </button>
                        ))}

                        <span className='grid w-8 place-items-center' style={{ color: theme.muted }}>
                            <SlidersHorizontal aria-hidden='true' className='size-3.5' />
                        </span>
                    </div>

                    <div>
                        <div className='flex items-center justify-between text-[11px]' style={{ color: theme.muted }}>
                            <span>Bet amount</span>
                            <span>Credits</span>
                        </div>
                        <div className='mt-1.5 flex h-10 items-center justify-between pl-3 pr-1' style={{ background: theme.panel, borderRadius: control }}>
                            <span className='text-sm' style={{ fontFamily: NUMERIC }}>10.00</span>
                            <span className='flex gap-1'>
                                {['½', '2×'].map(label => (
                                    <span key={label} className='grid h-8 w-8 place-items-center text-[11px]' style={{ background: theme.surface, borderRadius: Math.max(0, control - 3) }}>
                                        {label}
                                    </span>
                                ))}
                            </span>
                        </div>
                    </div>

                    <div className='grid grid-cols-2 gap-2'>
                        {[{ label: 'Roll over', value: '50.00' }, { label: 'Payout', value: '×1.98' }].map(field => (
                            <div key={field.label}>
                                <div className='text-[11px]' style={{ color: theme.muted }}>{field.label}</div>
                                <div className='mt-1.5 flex h-10 items-center px-3 text-sm' style={{ background: theme.panel, borderRadius: control, fontFamily: NUMERIC }}>
                                    {field.value}
                                </div>
                            </div>
                        ))}
                    </div>

                    <button
                        type='button'
                        onClick={placeBet}
                        className='mt-1 h-12 text-sm font-semibold uppercase tracking-[0.08em] transition-[transform,filter] hover:brightness-110 active:scale-[0.98]'
                        style={{
                            background: theme.accent,
                            color: theme.accentInk,
                            borderRadius: keyRadius,
                            boxShadow: `0 14px 34px -14px ${withAlpha(theme.accent, 0.9)}`
                        }}
                    >
                        Bet
                    </button>
                </div>

                <div className='relative m-3 mt-0 min-h-[230px] overflow-hidden md:ml-0 md:mt-3' style={{ background: STAGE, borderRadius: Math.max(0, radius - 6) }}>
                    <div
                        aria-hidden='true'
                        className='absolute inset-x-0 top-0 h-px'
                        style={{ background: `linear-gradient(90deg, transparent, ${theme.stageAccent}, transparent)`, boxShadow: `0 0 18px 1px ${withAlpha(theme.stageAccent, 0.6)}` }}
                    />

                    <div className='flex h-full flex-col justify-center gap-5 p-6'>
                        <div className='text-center'>
                            <span className='text-5xl tabular-nums tracking-tight transition-colors duration-300' style={{ fontFamily: NUMERIC, color: win ? WIN : 'rgba(244, 242, 238, 0.45)' }}>
                                <CountUp value={roll} replay={rolls} duration={650} />
                            </span>
                            <div className='mt-2 text-[11px]' style={{ color: '#9A9893', fontFamily: NUMERIC }}>
                                {win ? 'Win ×1.98' : 'No win, roll again'}
                            </div>
                        </div>

                        <div className='relative'>
                            <div className='flex h-2.5 overflow-hidden' style={{ borderRadius: Math.max(control, 2) }}>
                                <div className='h-full w-1/2 bg-[#26262c]' />
                                <div className='h-full flex-1 transition-colors duration-300' style={{ background: theme.stageAccent }} />
                            </div>

                            <div
                                aria-hidden='true'
                                className='absolute top-1/2 size-7 -translate-x-1/2 -translate-y-1/2 border border-white/30 bg-[#F4F2EE] shadow-[0_8px_20px_-6px_rgba(0,0,0,0.9)] transition-[left] duration-700 ease-[cubic-bezier(0.2,0.9,0.25,1.12)]'
                                style={{ left: `${roll}%`, borderRadius: Math.max(4, control * 0.6) }}
                            />
                        </div>

                        <div className='flex justify-between text-[10px] text-[#77756f]' style={{ fontFamily: NUMERIC }}>
                            {[0, 25, 50, 75, 100].map(tick => (
                                <span key={tick}>{tick}</span>
                            ))}
                        </div>
                    </div>
                </div>
            </div>

            <div className='flex h-10 items-center justify-between border-t px-4 text-[11px]' style={{ borderColor: theme.line, color: theme.muted }}>
                <span className='flex items-center gap-1.5'>
                    <ShieldCheck aria-hidden='true' className='size-3.5' />
                    Provably fair
                </span>
                <span style={{ fontFamily: NUMERIC }}>og-v1-h512</span>
            </div>
        </div>
    )
}

export default ThemePreview
